import { OutlinedCard } from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../graphql';
import {
    type GetBasicPrescriberOrdersByPatientQuery,
    type GetBasicPrescriberOrdersQuery,
    type GetPatientPriorAuthorizationsQuery,
    type PriorAuthorizationInfoFragment,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { usePatientContext } from '../../hooks/use-patient-context';
import { usePrescriberOrders } from '../../hooks/use-prescriber-orders';
import { isNewOrder } from '../../util/orders';
import { MedicationOrders } from '../cabinet/orders-card/MedicationOrders';
import { OrdersCardHeader } from '../cabinet/orders-card/OrdersCardHeader';

import { HomeCabinetNoOrdersCard } from './HomeCabinetNoOrdersCard';
import { HomeCabinetUpToDateCard } from './HomeCabinetUpToDateCard';

export type HomeMedCabCardProps = {
    patient:
        | NonNullable<GetBasicPrescriberOrdersQuery['patient']>
        | NonNullable<GetBasicPrescriberOrdersByPatientQuery['patient']>;
    patientId: string;
    showAll?: boolean;
    nonDrxPatientPriorAuthorizations: NonNullable<
        GetPatientPriorAuthorizationsQuery['patient']
    >;
};

export const HomeMedCabCard = ({
    patient,
    patientId,
    showAll,
    nonDrxPatientPriorAuthorizations,
}: HomeMedCabCardProps) => {
    const { nonDrxPriorAuthorizationEnabled } = useFlags();
    const { primaryPatientId } = usePatientContext();
    const nonDrxPriorAuths = getFragment(
        nonDrxPatientPriorAuthorizations.priorAuthorizationsConnection
    ).edges.map((edge) => edge.node as PriorAuthorizationInfoFragment);

    const orders = usePrescriberOrders({
        patient,
        patientId,
        primaryPatientId,
        nonDrxPriorAuthorizations: nonDrxPriorAuths,
    });

    const filteredOrders = nonDrxPriorAuthorizationEnabled
        ? orders
        : orders.filter(
              (order) =>
                  !(
                      'applicablePriorAuthorization' in order &&
                      getFragment(order.applicablePriorAuthorization)
                          ?.isSpecialty === false
                  )
          );

    const newOrders = filteredOrders.filter(isNewOrder);

    if (orders.length === 0) {
        return <HomeCabinetNoOrdersCard />;
    }

    if (newOrders.length === 0) {
        return <HomeCabinetUpToDateCard />;
    }

    return (
        <OutlinedCard
            header={<OrdersCardHeader count={newOrders.length} />}
            role="list"
        >
            <MedicationOrders orders={newOrders} showAll={showAll} />
        </OutlinedCard>
    );
};
